// ECharts Wrapper Component for Preact
// Provides responsive, theme-aware chart rendering with Batemo styling

import { useEffect, useRef, useState } from 'preact/hooks'
import * as echarts from 'echarts'
import { appliedTheme } from '../../state/themeStore.js'

/**
 * Batemo Dark Theme for ECharts
 */
const BATEMO_DARK_THEME = {
  color: [
    '#ea5b0c', // Batemo Orange (primary)
    '#0d7c99', // Batemo Cyan (accent)
    '#4ade80', // Green
    '#fbbf24', // Yellow
    '#f87171', // Red
    '#a78bfa', // Purple
    '#fb923c', // Orange light
    '#38bdf8', // Sky blue
  ],
  backgroundColor: 'transparent',
  textStyle: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: 12,
    color: '#cbd5e1', // text-secondary
  },
  title: {
    textStyle: {
      fontFamily: 'Big John, sans-serif',
      fontSize: 18,
      color: '#f1f5f9', // text-primary
      fontWeight: 'normal',
    },
    subtextStyle: {
      color: '#94a3b8', // text-tertiary
    },
  },
  line: {
    itemStyle: {
      borderWidth: 2,
    },
    lineStyle: {
      width: 2,
    },
    symbolSize: 6,
    symbol: 'circle',
    smooth: false,
  },
  radar: {
    itemStyle: {
      borderWidth: 2,
    },
    lineStyle: {
      width: 2,
    },
    symbolSize: 6,
    symbol: 'circle',
    smooth: false,
  },
  bar: {
    itemStyle: {
      barBorderWidth: 0,
      barBorderColor: '#475569',
    },
  },
  pie: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#475569',
    },
  },
  scatter: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#475569',
    },
  },
  boxplot: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#475569',
    },
  },
  parallel: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#475569',
    },
  },
  sankey: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#475569',
    },
  },
  funnel: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#475569',
    },
  },
  gauge: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#475569',
    },
  },
  candlestick: {
    itemStyle: {
      color: '#4ade80',
      color0: '#f87171',
      borderColor: '#4ade80',
      borderColor0: '#f87171',
      borderWidth: 1,
    },
  },
  graph: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#475569',
    },
    lineStyle: {
      width: 1,
      color: '#64748b',
    },
    symbolSize: 6,
    symbol: 'circle',
    smooth: false,
    color: [
      '#ea5b0c',
      '#0d7c99',
      '#4ade80',
      '#fbbf24',
      '#f87171',
      '#a78bfa',
    ],
    label: {
      color: '#cbd5e1',
    },
  },
  map: {
    itemStyle: {
      areaColor: '#1e293b',
      borderColor: '#475569',
      borderWidth: 0.5,
    },
    label: {
      color: '#f1f5f9',
    },
    emphasis: {
      itemStyle: {
        areaColor: '#ea5b0c',
        borderColor: '#f59e0b',
        borderWidth: 1,
      },
      label: {
        color: '#ffffff',
      },
    },
  },
  geo: {
    itemStyle: {
      areaColor: '#1e293b',
      borderColor: '#475569',
      borderWidth: 0.5,
    },
    label: {
      color: '#f1f5f9',
    },
    emphasis: {
      itemStyle: {
        areaColor: '#ea5b0c',
        borderColor: '#f59e0b',
        borderWidth: 1,
      },
      label: {
        color: '#ffffff',
      },
    },
  },
  categoryAxis: {
    axisLine: {
      show: true,
      lineStyle: {
        color: '#475569',
      },
    },
    axisTick: {
      show: true,
      lineStyle: {
        color: '#475569',
      },
    },
    axisLabel: {
      show: true,
      color: '#94a3b8',
    },
    splitLine: {
      show: false,
      lineStyle: {
        color: ['#334155'],
      },
    },
    splitArea: {
      show: false,
      areaStyle: {
        color: ['rgba(15,23,42,0.3)', 'rgba(30,41,59,0.3)'],
      },
    },
  },
  valueAxis: {
    axisLine: {
      show: false,
      lineStyle: {
        color: '#475569',
      },
    },
    axisTick: {
      show: false,
      lineStyle: {
        color: '#475569',
      },
    },
    axisLabel: {
      show: true,
      color: '#94a3b8',
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: ['#334155'],
      },
    },
    splitArea: {
      show: false,
      areaStyle: {
        color: ['rgba(15,23,42,0.3)', 'rgba(30,41,59,0.3)'],
      },
    },
  },
  logAxis: {
    axisLine: {
      show: false,
      lineStyle: {
        color: '#475569',
      },
    },
    axisTick: {
      show: false,
      lineStyle: {
        color: '#475569',
      },
    },
    axisLabel: {
      show: true,
      color: '#94a3b8',
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: ['#334155'],
      },
    },
    splitArea: {
      show: false,
      areaStyle: {
        color: ['rgba(15,23,42,0.3)', 'rgba(30,41,59,0.3)'],
      },
    },
  },
  timeAxis: {
    axisLine: {
      show: true,
      lineStyle: {
        color: '#475569',
      },
    },
    axisTick: {
      show: true,
      lineStyle: {
        color: '#475569',
      },
    },
    axisLabel: {
      show: true,
      color: '#94a3b8',
    },
    splitLine: {
      show: false,
      lineStyle: {
        color: ['#334155'],
      },
    },
    splitArea: {
      show: false,
      areaStyle: {
        color: ['rgba(15,23,42,0.3)', 'rgba(30,41,59,0.3)'],
      },
    },
  },
  toolbox: {
    iconStyle: {
      borderColor: '#94a3b8',
    },
    emphasis: {
      iconStyle: {
        borderColor: '#ea5b0c',
      },
    },
  },
  legend: {
    textStyle: {
      color: '#cbd5e1',
    },
  },
  tooltip: {
    backgroundColor: 'rgba(15, 23, 42, 0.95)',
    borderColor: '#475569',
    borderWidth: 1,
    textStyle: {
      color: '#f1f5f9',
    },
  },
  timeline: {
    lineStyle: {
      color: '#475569',
      width: 1,
    },
    itemStyle: {
      color: '#475569',
      borderWidth: 1,
    },
    controlStyle: {
      color: '#475569',
      borderColor: '#475569',
      borderWidth: 0.5,
    },
    checkpointStyle: {
      color: '#ea5b0c',
      borderColor: '#f59e0b',
    },
    label: {
      color: '#94a3b8',
    },
    emphasis: {
      itemStyle: {
        color: '#ea5b0c',
      },
      controlStyle: {
        color: '#475569',
        borderColor: '#475569',
        borderWidth: 0.5,
      },
      label: {
        color: '#94a3b8',
      },
    },
  },
  visualMap: {
    textStyle: {
      color: '#cbd5e1',
    },
    inRange: {
      color: ['#1e40af', '#0d7c99', '#4ade80', '#fbbf24', '#ea5b0c'],
    },
  },
  dataZoom: {
    backgroundColor: 'rgba(30,41,59,0.3)',
    dataBackgroundColor: 'rgba(100,116,139,0.3)',
    fillerColor: 'rgba(234,91,12,0.2)',
    handleColor: '#ea5b0c',
    handleSize: '100%',
    textStyle: {
      color: '#94a3b8',
    },
    borderColor: '#475569',
  },
  markPoint: {
    label: {
      color: '#f1f5f9',
    },
    emphasis: {
      label: {
        color: '#f1f5f9',
      },
    },
  },
}

/**
 * Batemo Light Theme for ECharts
 */
const BATEMO_LIGHT_THEME = {
  ...BATEMO_DARK_THEME,
  backgroundColor: 'transparent',
  textStyle: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: 12,
    color: '#475569',
  },
  title: {
    textStyle: {
      fontFamily: 'Big John, sans-serif',
      fontSize: 18,
      color: '#0f172a',
      fontWeight: 'normal',
    },
    subtextStyle: {
      color: '#64748b',
    },
  },
  categoryAxis: {
    ...BATEMO_DARK_THEME.categoryAxis,
    axisLine: {
      show: true,
      lineStyle: {
        color: '#cbd5e1',
      },
    },
    axisTick: {
      show: true,
      lineStyle: {
        color: '#cbd5e1',
      },
    },
    axisLabel: {
      show: true,
      color: '#64748b',
    },
    splitLine: {
      show: false,
      lineStyle: {
        color: ['#e2e8f0'],
      },
    },
  },
  valueAxis: {
    ...BATEMO_DARK_THEME.valueAxis,
    axisLabel: {
      show: true,
      color: '#64748b',
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: ['#e2e8f0'],
      },
    },
  },
}

/**
 * ECharts Component
 * 
 * @param {Object} props
 * @param {Object} props.option - ECharts option object
 * @param {string} props.style - Inline styles for container
 * @param {string} props.className - CSS class for container
 * @param {boolean} props.notMerge - Whether to merge option (default: false)
 * @param {boolean} props.lazyUpdate - Whether to update lazily (default: false)
 * @param {Function} props.onChartReady - Callback when chart is initialized
 */
export default function EChartsComponent({ 
  option, 
  style = {}, 
  className = '', 
  notMerge = false,
  replaceMerge = null,
  lazyUpdate = false,
  onChartReady = null 
}) {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)
  const [isChartReady, setIsChartReady] = useState(false)
  
  // Initialize chart
  useEffect(() => {
    if (!chartRef.current) return
    
    let retryCount = 0
    const maxRetries = 20
    
    // Wait for next frame to ensure DOM is fully rendered with dimensions
    const initChart = () => {
      if (!chartRef.current) return
      
      // Check if element has dimensions
      const { clientWidth, clientHeight } = chartRef.current
      if (clientWidth === 0 || clientHeight === 0) {
        if (retryCount >= maxRetries) {
          console.error('[ECharts] Container still has no dimensions after', maxRetries, 'retries. Giving up.')
          return
        }
        retryCount++
        // Retry after a short delay
        setTimeout(initChart, 50)
        return
      }
      
      console.log(`[ECharts] Container ready: ${clientWidth}x${clientHeight}`)
      
      // Register themes
      echarts.registerTheme('batemo-dark', BATEMO_DARK_THEME)
      echarts.registerTheme('batemo-light', BATEMO_LIGHT_THEME)
      
      // Create chart instance with theme
      const theme = appliedTheme.value === 'dark' ? 'batemo-dark' : 'batemo-light'
      chartInstance.current = echarts.init(chartRef.current, theme, {
        renderer: 'canvas',
        useDirtyRect: true, // Performance optimization
      })
      
      console.log('[ECharts] Chart instance created')
      setIsChartReady(true)
      
      // Callback when ready
      if (onChartReady) {
        onChartReady(chartInstance.current)
      }
    }
    
    // Use requestAnimationFrame to wait for layout
    requestAnimationFrame(() => {
      requestAnimationFrame(initChart)
    })
    
    // Cleanup
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose()
        chartInstance.current = null
      }
      setIsChartReady(false)
    }
  }, [])
  
  // Update chart option
  useEffect(() => {
    if (!isChartReady || !chartInstance.current) {
      console.warn('[ECharts] Chart instance not ready yet, waiting...')
      return
    }
    if (!option) {
      console.warn('[ECharts] No option provided')
      return
    }
    
    console.log('[ECharts] Setting option:', {
      hasOption: !!option,
      seriesCount: option.series?.length,
      seriesDetails: option.series?.map(s => ({ 
        id: s.id, 
        name: s.name, 
        dataPoints: s.data?.length 
      })),
      legendCategories: option.legend?.data?.length,
      notMerge,
      replaceMerge,
      title: option.title?.text
    })
    
    chartInstance.current.setOption(option, {
      notMerge,
      replaceMerge,
      lazyUpdate,
    })
    
    console.log('[ECharts] ✓ Option set successfully')
  }, [isChartReady, option, notMerge, replaceMerge, lazyUpdate])
  
  // Handle resize — must re-run when chart becomes ready (isChartReady)
  // so the ResizeObserver is connected after async chart initialization
  useEffect(() => {
    if (!chartInstance.current) return

    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    // Create ResizeObserver for container size changes
    // This catches CSS transition resizes (e.g. settings drawer slide-in)
    const resizeObserver = new ResizeObserver(() => {
      handleResize()
    })

    if (chartRef.current) {
      resizeObserver.observe(chartRef.current)
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
    }
  }, [isChartReady])
  
  // Handle theme changes
  useEffect(() => {
    if (!chartInstance.current) return
    
    const unsubscribe = appliedTheme.subscribe((newTheme) => {
      if (!chartInstance.current || !chartRef.current) return
      // Dispose and recreate chart with new theme
      const currentOption = chartInstance.current.getOption()
      chartInstance.current.dispose()
      
      const theme = newTheme === 'dark' ? 'batemo-dark' : 'batemo-light'
      chartInstance.current = echarts.init(chartRef.current, theme, {
        renderer: 'canvas',
        useDirtyRect: true,
      })
      
      chartInstance.current.setOption(currentOption)
    })
    
    return unsubscribe
  }, [])
  
  return (
    <div 
      ref={chartRef}
      className={`echarts-container ${className}`}
      style={{ 
        width: '100%', 
        height: '100%',
        minHeight: '400px',
        minWidth: '400px',
        flex: 1,
        ...style 
      }}
    />
  )
}
